// Global Instructions Rule Applied!

import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import Select from '../../Select.jsx'
import { InputSize, FieldValue } from '../types.ts'

export interface TagsInputProps {
  value: FieldValue
  onChange: (value: string[]) => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  size?: InputSize
  status?: 'error' | 'warning'
  placeholder?: string
  maxLength?: number
  disabled?: boolean
  autoFocus?: boolean
}

export const TagsInput = forwardRef<any, TagsInputProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      size = 'small',
      status,
      placeholder = 'Enter tags separated by commas...',
      maxLength,
      disabled,
      autoFocus
    },
    ref
  ) => {
    const selectRef = useRef<any>(null)
    const tags = Array.isArray(value) ? value : value ? [value] : []
    const [currentInput, setCurrentInput] = useState<string>('')

    useImperativeHandle(ref, () => ({
      focus: () => {
        selectRef.current?.focus()
      },
      blur: () => {
        selectRef.current?.blur()
      }
    }))

    const handleChange = (newTags: string[]) => {
      // Filter out empty strings and trim values
      const filteredTags = newTags
        .map((tag) => (typeof tag === 'string' ? tag.trim() : String(tag).trim()))
        .filter((tag) => tag !== '')

      // Apply maxLength if specified
      const finalTags = maxLength ? filteredTags.slice(0, maxLength) : filteredTags
      onChange(finalTags)
    }

    const handleSearch = (searchValue: string) => {
      // Track current input value for Enter key handling
      setCurrentInput(searchValue)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        // Check if the event is coming from the Select's input element
        const target = e.target as HTMLElement
        const isSelectInput = target.tagName === 'INPUT' && target.closest('.ant-select')

        if (isSelectInput && currentInput && currentInput.trim() !== '') {
          // Prevent default behavior
          e.preventDefault()
          e.stopPropagation()

          // Add the current input as a new tag
          const trimmedValue = currentInput.trim()
          if (!tags.includes(trimmedValue)) {
            const newTags = [...tags, trimmedValue]
            handleChange(newTags)

            // Clear the input by resetting search
            setCurrentInput('')

            // Clear the Select's internal input value
            const selectElement = selectRef.current
            if (selectElement) {
              const selectInput = selectElement.querySelector?.('input') as HTMLInputElement
              if (selectInput) {
                selectInput.value = ''
              }
            }
          }

          // Don't propagate to parent onKeyDown handler
          return
        }
      }

      // For other keys or when not creating a tag, call parent handler
      onKeyDown?.(e)
    }

    return (
      <div onKeyDown={handleKeyDown}>
        <Select
          ref={selectRef}
          mode='tags'
          value={tags}
          onChange={handleChange}
          onSearch={handleSearch}
          size={size}
          status={status}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          style={{ width: '100%' }}
          tokenSeparators={[',']}
          allowClear={false}
        />
      </div>
    )
  }
)

TagsInput.displayName = 'TagsInput'
